import { useMemo, useState, useEffect } from "react";
import styles from "./WizardHeaderPagination.module.css";
import { Row } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { COI_REVIEW_PURPOSE, WizardHeaderPaginationProps } from "./WizardHeaderPagination.utils";
import CustomButton from "../custom_button_component/Button";
import { WizardSharedData } from "../WizardPageController/WizardPageController.utils";

function WizardHeaderPagination<CustomSharedData extends WizardSharedData>({
  config,
  purpose,
  activeStep,
  setActiveStep,
  sharedData,
  setSharedData,
  onComplete,
  onSaveAsDraft,
  coiSaving,
  coiAllDecided,
  onCoiSaveDraft,
  onCoiSubmit,
}: WizardHeaderPaginationProps<CustomSharedData>) {
  const [approveLoader, setApproveLoader] = useState(false);
  const [rejectLoader, setRejectLoader] = useState(false);

  useEffect(() => {
    if (!sharedData.approvalAPIProgress) {
      setApproveLoader(false);
      setRejectLoader(false);
    }
  }, [sharedData.approvalAPIProgress]);

  const pages = useMemo(() => {
    return config
      .filter((page) => page.show)
      .sort((a, b) => a.order - b.order)
      .map((page, index) => ({
        id: index + 1,
        label: page.label,
        validate: page.validate,
        flag: sharedData?.validations?.[index] ?? false,
      }));
  }, [config, sharedData?.validations]);

  const isCurrentPageValid = useMemo(() => {
    const currentConfig = config[activeStep];
    if (!currentConfig) return false;
    if (currentConfig.validate === false) return true;
    return sharedData?.validations?.[activeStep] ?? false;
  }, [activeStep, config, sharedData?.validations]);

  const handleNext = (status?: "Approved" | "Rejected") => {
    const currentConfig = config[activeStep];
    const isLastPage = activeStep === pages.length - 1;

    if (currentConfig?.validate === false) {
      setSharedData((prev) => ({
        ...prev,
        validations: { ...prev.validations, [activeStep]: true },
      }));
    }

    const isCurrentValid =
      currentConfig?.validate === false
        ? true
        : sharedData?.validations?.[activeStep] ?? false;

    if (!isCurrentValid) return;

    if (!status && !isLastPage) {
      setActiveStep(activeStep + 1);
    } else {
      onComplete?.(sharedData, status);
    }

    if (status === "Approved") {
      setRejectLoader(false);
      setApproveLoader(true);
    }
    else if (status === "Rejected") {
      setApproveLoader(false);
      setRejectLoader(true);
    }
  };


  const handlePrev = () => {
    if (activeStep > 0) setActiveStep(activeStep - 1);
  };

  return (
    <Row className={styles.wizardPaginationWrapper}>
      <div className={styles.leftButtons}>
        <CustomButton
          content="Previous"
          onclick={handlePrev}
          disabled={activeStep === 0 || sharedData?.isEditorLoading}
        />
      </div>

      <div className={styles.wizardPaginationHolder}>
        {pages.map((page, index) => (
          <div className={styles.wizardPageTitleWrapper} key={page.id}>
            <div
              className={`${styles.wizardPageTitleContainer} ${page.flag ? styles.completed : ""
                } ${index === activeStep ? styles.current : ""}`}
            >
              {page.flag ? (
                <>
                  <Icon.CheckAll className={styles.rotatingChecks} /> {page.label}
                </>
              ) : (
                page.label
              )}
            </div>

            {index < pages.length - 1 && (
              <div
                className={`${styles.wizardPaginationConnector} ${page.flag
                    ? styles.active
                    : index === activeStep
                      ? styles.current
                      : ""
                  }`}
              />
            )}
          </div>
        ))}
      </div>

      {purpose === "Client Approval" ? (
        <div className={styles.rightButtons}>
          {
          sharedData?.purpose &&
            <div className={styles.losContainer}>
              <h5 className={`${styles.losBadge}`}>
                <span className={styles.label}>Line of service:</span>
                <span className={styles.value}>{sharedData.purpose.toUpperCase()}</span>
              </h5>
            </div>
          }
          <CustomButton
            content={(activeStep !== pages.length - 1) ? "Next" : "Approve"}
            onclick={() => handleNext((activeStep !== pages.length - 1) ? undefined : "Approved")}
            disabled={!isCurrentPageValid || sharedData?.isEditorLoading}
            progress={sharedData.approvalAPIProgress && approveLoader}
          />
          <CustomButton
            content={"Reject"}
            onclick={() => handleNext("Rejected")}
            disabled={!isCurrentPageValid}
            progress={sharedData.approvalAPIProgress && rejectLoader}
          />
        </div>
      ) : purpose === COI_REVIEW_PURPOSE ? (
        <div className={styles.rightButtons}>
          {
          sharedData?.purpose &&
            <div className={styles.losContainer}>
              <h5 className={`${styles.losBadge}`}>
                <span className={styles.label}>Line of service:</span>
                <span className={styles.value}>{sharedData.purpose.toUpperCase()}</span>
              </h5>
            </div>
          }
          {activeStep !== pages.length - 1 ? (
            <CustomButton
              content="Next"
              onclick={() => handleNext()}
              disabled={!isCurrentPageValid || sharedData?.isEditorLoading}
            />
          ) : (
            <>
              <CustomButton
                content="Save Draft"
                progress={coiSaving === "draft"}
                disabled={coiSaving != null}
                onclick={() => onCoiSaveDraft?.()}
              />
              <CustomButton
                content="Submit"
                progress={coiSaving === "submit"}
                disabled={coiSaving != null || !coiAllDecided}
                onclick={() => onCoiSubmit?.()}
              />
            </>
          )}
        </div>
      ) : (
        <div className={styles.rightButtons}>
          {sharedData?.currentPage === "fillInfo" && (
            <CustomButton
              onclick={() => setSharedData((prev) => ({ ...prev, sync: true }))}
              disabled={
                sharedData?.isEditorLoading ||
                activeStep === pages.length - 1 && sharedData?.disablePreview
              }
              content={"Preview Draft"}
            />
          )}
          {activeStep === pages.length - 1 && onSaveAsDraft && (
            <CustomButton
              progress={sharedData.draftApiProgress}
              content={"Save as Draft"}
              onclick={onSaveAsDraft}
              disabled={sharedData?.isEditorLoading || sharedData?.saveApiProgress}
            />
          )}
          <CustomButton
            progress={
              activeStep === pages.length - 1 ? sharedData.saveApiProgress : false
            }
            content={activeStep === pages.length - 1 ? "Submit" : "Next"}
            onclick={() => handleNext()}
            disabled={sharedData?.isEditorLoading || sharedData?.draftApiProgress}
          />
        </div>
      )}
    </Row>
  );
}

export default WizardHeaderPagination;
